/**
 * Filter Storage Utility
 * Manages persistence of A/B test filter state in localStorage
 */

interface FilterState {
  search: string;
  status: string | string[]; // Support both legacy string and new array format
  applications: number[];
  platforms: number[];
}

const STORAGE_KEY = "ab-testing-filters";

/**
 * Save filter state to localStorage
 * @param filters - Current filter state with Sets converted to arrays
 */
export const saveFilterState = (filters: {
  search: string;
  status: Set<string>;
  applications: Set<number>;
  platforms: Set<number>;
}): void => {
  try {
    const filterState: FilterState = {
      search: filters.search,
      status: Array.from(filters.status), // Convert Set to array for storage
      applications: Array.from(filters.applications),
      platforms: Array.from(filters.platforms),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));
  } catch (error) {
    // Handle storage quota exceeded or other errors
    console.warn("Failed to save filter state:", error);
  }
};

/**
 * Load filter state from localStorage
 * @returns Saved filter state or null if not found/invalid
 */
export const loadFilterState = (): FilterState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);

    // Validate the structure and handle backwards compatibility
    if (
      typeof parsed.search === "string" &&
      (typeof parsed.status === "string" || Array.isArray(parsed.status)) &&
      Array.isArray(parsed.applications) &&
      Array.isArray(parsed.platforms)
    ) {
      // Convert legacy string status to array format
      if (typeof parsed.status === "string") {
        if (parsed.status === "all") {
          parsed.status = []; // "all" means no specific filter selected
        } else {
          parsed.status = [parsed.status]; // Convert single status to array
        }
      }
      return parsed;
    }

    return null;
  } catch (error) {
    console.warn("Failed to load filter state:", error);
    return null;
  }
};

/**
 * Clear saved filter state from localStorage
 */
export const clearFilterState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear filter state:", error);
  }
};

/**
 * Validate and clean filter state against available options
 * @param savedState - Saved filter state
 * @param availableApps - List of available application IDs
 * @param availablePlatforms - List of available platform IDs
 * @param validStatuses - List of valid status values
 * @returns Cleaned filter state
 */
export const validateFilterState = (
  savedState: FilterState,
  availableApps: number[],
  availablePlatforms: number[],
  validStatuses: string[]
): {
  search: string;
  status: Set<string>;
  applications: number[];
  platforms: number[];
} => {
  // Ensure status is in array format (for backwards compatibility)
  let statusArray: string[] = [];
  if (typeof savedState.status === "string") {
    if (
      savedState.status !== "all" &&
      validStatuses.includes(savedState.status)
    ) {
      statusArray = [savedState.status];
    }
  } else if (Array.isArray(savedState.status)) {
    // Filter out "all" and invalid statuses, keeping only valid ones
    statusArray = savedState.status.filter(
      (s) => s !== "all" && validStatuses.includes(s)
    );
  }

  return {
    search: savedState.search || "",
    status: new Set(statusArray),
    applications: savedState.applications.filter((id) =>
      availableApps.includes(id)
    ),
    platforms: savedState.platforms.filter((id) =>
      availablePlatforms.includes(id)
    ),
  };
};
